import { FitnessPlan, parseSubslotIdAndOffset } from '@cf-shared/data-model/fitness-plan';
import { isCustomExerciseById } from '@cf-shared/data-model/exercise';
import { isSplitSubSlotId, Log_WorkoutExercise } from '@cf-shared/data-model/log-workout';
import { LogRecordState } from '@cf-shared/data-model/user-log';
import { DisplaySubSlotSource } from 'util/historical-subslot';
import { createSessionDedup, _resetAllSessionDedupsForTesting } from 'util/session-dedup';
import { SessionHealth } from 'services/session-health';
import type { SentryReportTags } from 'services/problem-kind';
import { CustomExerciseAttribution, resolveSimilarExercise } from 'util/muscle-attribution';

// Structural minimum needed from LoggingService — kept here so this module has zero Angular-DI
// dependencies and can be unit-tested under the ts-node ESM loader. The real LoggingService
// satisfies this shape.
export interface OrphanReporter {
  trackSilentProblem(name: string, context: any, tags?: SentryReportTags, sessionHealth?: SessionHealth): void;
}

// #4614 needs the info tier as well, because it reports a repair that already succeeded.
// Kept as a separate interface so the existing reporters below keep their narrower
// requirement and their tests keep their smaller stub.
export interface OrphanInfoReporter extends OrphanReporter {
  trackSilentInfo(name: string, context: any): void;
}

// #3282: dedup so each orphan id is reported at most once per process / browser-tab lifetime
// (cleared on tab reload, not on logout/login — login granularity isn't needed here).
// buildMuscleVolumeEntries is called from 4 client sites (edit-workouts-page,
// muscle-volume-page recompute loop, program-settings-page ×2) and reportOrphanSubSlotIds is
// also called directly from workout-loader.assembleWorkoutDetailsAsync — without dedup a
// single tab session with one orphan would fire trackSilentProblem many times for the same
// condition. One Sentry event per orphan is enough to discriminate the upstream candidate;
// further events are noise.
const reportedOrphanSubSlotIds = createSessionDedup();

// #3282: detect subSlotIds in workouts that have no matching entry in plan.subSlots, before
// calling getMuscleVolumeForPlan. The shared function skips them defensively (console.warn +
// continue), but client-side surfacing through LoggingService is what gives us the Sentry
// breadcrumb to identify which upstream path produced the orphan in production.
export function reportOrphanSubSlotIds(fitnessPlan: FitnessPlan, logging: OrphanReporter, callSite: string): void {
  if (!fitnessPlan.workouts || !fitnessPlan.subSlots) return;
  const totalSubSlots = Object.keys(fitnessPlan.subSlots).length;
  for (let i = 0; i < fitnessPlan.workouts.length; i++) {
    const workout = fitnessPlan.workouts[i];
    if (!workout.subSlotIds) continue;
    for (const subSlotId of workout.subSlotIds) {
      if (fitnessPlan.subSlots[subSlotId]) continue;
      // dedup: report each orphan id at most once per session across all call sites
      if (!reportedOrphanSubSlotIds.firstTimeFor(subSlotId)) continue;
      // also note if the same id resolves elsewhere — distinguishes a missing-entry bug
      // from a multi-use-with-deletion bug
      const inAnotherWorkout = fitnessPlan.workouts.some((w, j) => j !== i && w.subSlotIds?.includes(subSlotId));
      logging.trackSilentProblem('edit-workouts.orphan-subslot', {
        callSite,
        subSlotId,
        workoutName: workout.name,
        workoutIndex: i,
        inAnotherWorkout,
        totalSubSlots,
        totalWorkouts: fitnessPlan.workouts.length,
      });
    }
  }
}

/** What the load-time heal in `UserDbService.loadPlanAsync` stripped out of one plan. */
export interface MissingSubSlotsHealed {
  // the sub-slot ids a workout referenced but the plan had no entry for
  missingIds: string[];
  // the workouts those ids were referenced from, once each
  workoutNames: string[];
  // both counted before the heal changed anything
  totalWorkouts: number;
  totalSubSlots: number;
}

/**
 * df-740e0f6a5cb7: report once that loading the plan dropped references to sub-slots the plan
 * no longer holds. One event per load with a stable name, so every occurrence groups into one
 * Sentry issue, and ids plus counts rather than the plan document, which is large enough to be
 * truncated or dropped by Sentry's event size limit.
 *
 * Problem tier, not info: the heal removes the exercise from the training day rather than
 * restoring it, so something is still wrong for the user (#4382).
 */
export function reportMissingSubSlotsHealedAtLoad(healed: MissingSubSlotsHealed, logging: OrphanReporter): void {
  if (healed.missingIds.length === 0) return;
  logging.trackSilentProblem('plan.missing-subslot-at-load', {
    missingIds: healed.missingIds,
    workoutNames: healed.workoutNames,
    totalWorkouts: healed.totalWorkouts,
    totalSubSlots: healed.totalSubSlots,
  });
}

// #3790: dedup so each uncounted subSlotId is reported at most once per tab session and reason
// (#6060), same rationale as the orphan dedup above (buildMuscleVolumeEntries runs from several
// call sites).
const reportedUncountedSubSlotIds = createSessionDedup();

/** Why the volume calculator could not attribute an exercise to any muscle (#6060). */
export enum UncountedReason {
  /** the user's own exercise, with no "Most Similar Exercise" set. Expected: the screen asks them to set one */
  OwnExerciseNoSimilar = 'OwnExerciseNoSimilar',
  /** a built-in exercise in a slot the program does not measure, or the user's own exercise whose
   *  "Most Similar Exercise" is in such a slot. Expected: the program does not count that muscle */
  NotMeasuredByProgram = 'NotMeasuredByProgram',
  /** the user's own exercise names a similar exercise, yet that gave it no muscle weights */
  OwnExerciseWithSimilar = 'OwnExerciseWithSimilar',
  /** the user's own exercise names another own exercise as similar, and following those links
   *  never reaches a built-in exercise: a loop, a missing document, or one with no similar set */
  OwnExerciseSimilarUnresolved = 'OwnExerciseSimilarUnresolved',
  /** the plan points at the user's own exercise, but its document is gone */
  OwnExerciseDocMissing = 'OwnExerciseDocMissing',
  /** anything else, e.g. a built-in exercise in `$custom` or in another slot with no constants entry */
  Other = 'Other',
}

/**
 * Slots whose exercises the program deliberately does not measure, so an exercise in one is
 * listed under "Not Measured by This Program" (#4737). Forearms and neck are not tracked muscles
 * (#4016, #5619). `$erector_spinae` is here only until #5059 decides whether back
 * hyper-extension gets a constants entry; remove it then.
 *
 * The same list decides the report for a user's own exercise whose "Most Similar Exercise" is in
 * one of these slots: it borrows no muscle weights, which is expected, so it reports at info level.
 *
 * An explicit list rather than "any slot with no constants entry", so a slot that loses its
 * entry by mistake still reports at problem level.
 */
export const NOT_MEASURED_SLOT_IDS: readonly string[] = ['$forearm', '$neck', '$erector_spinae'];

/** The part of the user's own exercise the classification reads, keyed by exercise id. */
export interface OwnExerciseAttribution {
  similarExerciseId?: string;
  /** the short slot id (`$neck`) the similar exercise belongs to, if it was looked up and found.
   *  When the link goes through other own exercises, this is the slot of the built-in one it reaches */
  similarSlotShortId?: string;
  /** set only when the link goes through other own exercises: the built-in exercise it reaches */
  resolvedSimilarExerciseId?: string;
  /** set only when the link goes through other own exercises and never reaches a built-in one:
   *  the own exercise ids walked, ending where it stopped */
  unresolvedSimilarChain?: string[];
}

/**
 * Build the per-own-exercise attribution the classification reads, from the user's own exercises
 * and the slot each one's "Most Similar Exercise" link led to (`similarSlots`, filled by the
 * muscle-factor overrides). When the link goes through other own exercises, it also says where
 * following it ended (CYBERNETIC-FITNESS-APP-463), using the same walk the overrides used.
 */
export function buildOwnExerciseAttributions(
  customExercises: CustomExerciseAttribution[],
  similarSlots: { [customExerciseId: string]: string },
): { [exerciseId: string]: OwnExerciseAttribution } {
  const ownExercises: { [exerciseId: string]: OwnExerciseAttribution } = {};
  const customExercisesById = new Map(customExercises.map(e => [e._id, e]));
  for (const customExercise of customExercises) {
    const attribution: OwnExerciseAttribution = {
      similarExerciseId: customExercise.similarExerciseId,
      similarSlotShortId: similarSlots[customExercise._id],
    };
    // if the similar exercise is another own exercise, say where following the links ended
    const resolution = resolveSimilarExercise(customExercise._id, customExercisesById);
    if (resolution && resolution.chain.length > 1) {
      if (resolution.catalogExerciseId) attribution.resolvedSimilarExerciseId = resolution.catalogExerciseId;
      else attribution.unresolvedSimilarChain = resolution.chain;
    }
    ownExercises[customExercise._id] = attribution;
  }
  return ownExercises;
}

/** Work out why a sub-slot the plan holds was left uncounted. */
export function uncountedReasonFor(
  subSlotId: string,
  fitnessPlan: FitnessPlan,
  ownExercises: { [exerciseId: string]: OwnExerciseAttribution },
): UncountedReason {
  const exerciseId = fitnessPlan.subSlots?.[subSlotId]?.exercise_id;
  if (!exerciseId) return UncountedReason.Other;

  // if it is the user's own exercise, the answer depends on its document
  if (isCustomExerciseById(exerciseId)) {
    const ownExercise = ownExercises[exerciseId];
    if (!ownExercise) return UncountedReason.OwnExerciseDocMissing;
    if (!ownExercise.similarExerciseId) return UncountedReason.OwnExerciseNoSimilar;
    if (ownExercise.unresolvedSimilarChain) return UncountedReason.OwnExerciseSimilarUnresolved;
    // if the similar exercise is in a slot the program does not measure, nothing went wrong:
    // it is the same state as that built-in exercise sitting in the plan
    const similarSlotId = ownExercise.similarSlotShortId;
    if (similarSlotId && NOT_MEASURED_SLOT_IDS.includes(similarSlotId)) return UncountedReason.NotMeasuredByProgram;
    return UncountedReason.OwnExerciseWithSimilar;
  }

  // a built-in exercise: read the slot off the sub-slot id, the way the volume calculator does
  const { parsedSlotId } = parseSubslotIdAndOffset(subSlotId);
  return NOT_MEASURED_SLOT_IDS.includes(parsedSlotId) ? UncountedReason.NotMeasuredByProgram : UncountedReason.Other;
}

// #3790: surface slots that the volume calculator could not attribute to any muscle — present in
// plan.subSlots, but with no constants-table entry and no muscle-factor override. These contribute
// zero to the displayed totals, so without this the user sees a silently-wrong number. This is
// "log and continue" telemetry; the user-facing surfacing is the "not counted" lists in the
// muscle-volume UI.
//
// #6060: the tier answers "is this condition still unresolved?", per CLAUDE.md. The two cases the
// screen already explains as normal are INFO. Every other reason is a PROBLEM: it is not a case
// the app has settled as expected, so someone may need to look at it.
//
// The dedup key carries the reason as well as the id: an exercise first seen in an expected state
// can turn into a fault later in the same tab (its document deleted, another exercise swapped into
// the sub-slot), and that later problem must still be reported.
export function reportUncountedSubSlotIds(
  subSlotIds: string[],
  fitnessPlan: FitnessPlan,
  ownExercises: { [exerciseId: string]: OwnExerciseAttribution },
  logging: OrphanInfoReporter,
  callSite: string,
): void {
  for (const subSlotId of subSlotIds) {
    const reason = uncountedReasonFor(subSlotId, fitnessPlan, ownExercises);
    if (!reportedUncountedSubSlotIds.firstTimeFor(subSlotId + '|' + reason)) continue;
    const context: { [key: string]: unknown } = {
      callSite,
      subSlotId,
      reason,
      totalUncounted: subSlotIds.length,
    };
    // the similar exercise is what failed to give weights, so name it for whoever reads this
    if (reason === UncountedReason.OwnExerciseWithSimilar) {
      const exerciseId = fitnessPlan.subSlots[subSlotId].exercise_id;
      context.similarExerciseId = ownExercises[exerciseId].similarExerciseId;
      // which slot it is in tells "no slot at all" from "a slot with no muscle weights"
      context.similarSlotShortId = ownExercises[exerciseId].similarSlotShortId;
      // if the link went through other own exercises, name the built-in one it reached
      const resolvedSimilarExerciseId = ownExercises[exerciseId].resolvedSimilarExerciseId;
      if (resolvedSimilarExerciseId) context.resolvedSimilarExerciseId = resolvedSimilarExerciseId;
    }
    // the chain of own exercises says where following the links stopped
    if (reason === UncountedReason.OwnExerciseSimilarUnresolved) {
      context.similarChain = ownExercises[fitnessPlan.subSlots[subSlotId].exercise_id].unresolvedSimilarChain;
    }
    if (reason === UncountedReason.OwnExerciseNoSimilar || reason === UncountedReason.NotMeasuredByProgram) {
      logging.trackSilentInfo('muscle-volume.uncounted-exercise', context);
    } else {
      logging.trackSilentProblem('muscle-volume.uncounted-exercise', context);
    }
  }
}

// #4614: dedup, same rationale as the two sets above. The key carries the outcome as well as
// the id, because the same exercise can legitimately produce different answers over a tab
// session (one workout of it carries a snapshot, an older one does not) and each is worth one
// event.
const reportedDisplaySubSlotOutcomes = createSessionDedup();

/**
 * #4614: report that a logged exercise's sub-slot is no longer in the user's program, and say
 * which of the standins was able to take its place.
 *
 * Call this ONLY when the program has no entry under that id. An exercise still in the program
 * is the ordinary case and reporting it would drown out everything else.
 *
 * The tiers answer "is this condition still unresolved?", per CLAUDE.md:
 *  - drawn from the snapshot, or rebuilt from the rest of the log -> INFO. The repair has
 *    already happened by the time this runs, the user sees their exercise, and nobody needs to
 *    act. It is worth recording only so we can see how widespread the removed-exercise case is,
 *    and which of the two repairs is carrying it.
 *  - neither, and the log entry holds no Done set -> INFO, under its own name
 *    (df-774d10f74c57). Nothing the user did is stored for that exercise (typically every set
 *    was Skipped), so there is nothing to draw and not drawing it is the accepted behaviour.
 *    The rebuild refuses such an entry on purpose; see `rebuildDisplaySubSlotFromLog`.
 *  - neither otherwise -> PROBLEM. The exercise is still being dropped from a session the user
 *    performed, or no log entry was passed to prove it was not. The context says what the log
 *    did carry, so the residue left after the rebuild can be diagnosed without another release.
 */
export function reportDisplaySubSlotMissingFromPlan(
  subSlotId: string,
  source: DisplaySubSlotSource,
  logging: OrphanInfoReporter,
  callSite: string,
  logExercise?: Log_WorkoutExercise,
): void {
  // #5507: an entry split off by an exercise change is never in the program, by design. Drawing
  // it from its snapshot or the log is the expected path, not a removed exercise, and counting it
  // would bury the signal this report exists for. One that could not be drawn at all still reports.
  if (isSplitSubSlotId(subSlotId) && source !== DisplaySubSlotSource.None) return;

  // df-774d10f74c57: an entry with no Done set was never performed, so not drawing it is expected.
  // Without a log entry nothing proves that, so it stays a problem.
  const doneSetsCount = logExercise?.sets?.filter(s => s?.state === LogRecordState.Done).length;
  const neverPerformed = source === DisplaySubSlotSource.None && !!logExercise && !doneSetsCount;

  // the outcome is part of the key, so an unperformed entry seen first cannot swallow a later
  // performed-and-dropped one for the same id
  const key = subSlotId + '|' + source + (neverPerformed ? '|unperformed' : '');
  if (!reportedDisplaySubSlotOutcomes.firstTimeFor(key)) return;

  const context = { callSite, subSlotId };
  if (source === DisplaySubSlotSource.LogSnapshot) {
    logging.trackSilentInfo('past-workout.subslot-drawn-from-snapshot', context);
  } else if (source === DisplaySubSlotSource.RebuiltFromLog) {
    logging.trackSilentInfo('past-workout.subslot-rebuilt-from-log', context);
  } else if (neverPerformed) {
    logging.trackSilentInfo('past-workout.unperformed-subslot-not-drawn', {
      ...context,
      setsCount: logExercise.sets?.length,
      skippedSetsCount: logExercise.sets?.filter(s => s?.state === LogRecordState.Skipped).length,
      hasExerciseId: !!logExercise.exerciseId,
    });
  } else {
    // what the log DID carry, so the remaining cases can be told apart. Without this the
    // report says only that something was dropped, which is what we already know.
    //
    // not the user's session going wrong: the exercise really is missing from a session they
    // performed, but that is a permanent property of workouts logged before snapshots existed,
    // not a fault in this session, and nothing can ever clear it. Counted, it would stop the
    // app asking those users to rate it, on every session, for ever.
    logging.trackSilentProblem('past-workout.subslot-lost', {
      ...context,
      hasExerciseId: !!logExercise?.exerciseId,
      hasPathLevel: logExercise?.pathLevel !== undefined,
      hasRecordedPath: !!logExercise?.sets?.some(s => s?.planPathBackup),
      doneSetsCount,
      setsCount: logExercise?.sets?.length,
    }, undefined, SessionHealth.NotTheUsersSession);
  }
}

// test-only: reset every per-session dedup so each test starts clean. It clears every dedup made
// with createSessionDedup, so a report added here later is covered without editing this.
export function _resetSubSlotReportDedupForTesting(): void {
  _resetAllSessionDedupsForTesting();
}
